import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Button,
  Card,
  Collapse,
  Dropdown,
  Grid,
  Input,
  Modal,
  Segmented,
  Space,
  Tag,
  Typography,
  message,
} from "antd";
import { ArrowLeftOutlined, EllipsisOutlined } from "@ant-design/icons";
import LoadingCard from "components/LoadingCard/LoadingCard";
import FieldInput, { CONTROL } from "./FieldInput";
import ListInput from "./ListInput";
import { LANGUAGES } from "./languages";
import {
  filterGroups,
  groupFields,
  initialValuesFromManifest,
  localiseManifest,
  overriddenCount,
  overriddenLabel,
  payloadFromValues,
  publishChangeCount,
  publishChangeGroups,
} from "./fieldValues";
import {
  errorMessage,
  fetchPageForEditor,
  savePageDraft,
  setPageStatus,
} from "./pagesApi";
import { assistTranslate } from "./translationApi";
import useLivePreview from "./useLivePreview";
import { focusTargetFor } from "./livePreview";
import useDraftAutosave from "./useDraftAutosave";
import LivePreviewPane from "./LivePreviewPane";
import "./MarketingPages.scss";
import { landingPageUrl } from "helpers/landingUrl";

const { Title, Text } = Typography;

/**
 * Which sections the editor has opened by hand, per page.
 *
 * Remembered because the work is repetitive: the same two or three sections get
 * revisited across sessions, and re-opening them every time is the kind of
 * friction that makes an editor stop using the tool. Every access is guarded —
 * a browser with storage disabled must degrade to the defaults, not crash the
 * screen.
 */
const openSectionsKey = (pageKey) => `hpn:marketing:open-sections:${pageKey}`;

const readOpenSections = (pageKey) => {
  try {
    const raw = window.localStorage.getItem(openSectionsKey(pageKey));
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed : null;
  } catch (error) {
    return null;
  }
};

const writeOpenSections = (pageKey, names) => {
  try {
    window.localStorage.setItem(
      openSectionsKey(pageKey),
      JSON.stringify(names),
    );
  } catch (error) {
    // Storage is a convenience here; losing it costs the editor one tap.
  }
};

/**
 * Which sections start open: the remembered set when there is one, otherwise
 * the sections that already hold edits. Collapsing everything else is what
 * turns a 64-field page into something scannable on a phone.
 */
const initialOpenSections = (pageKey, groups, values) => {
  const names = groups.map((group) => group.name);
  const stored = readOpenSections(pageKey);
  if (stored) return stored.filter((name) => names.includes(name));
  return groups
    .filter((group) => overriddenCount(group.fields, values) > 0)
    .map((group) => group.name);
};

/**
 * The Bulgarian draft, shaped for "Translate from BG": its stored values and
 * its committed fallbacks, both keyed by manifest path.
 */
const bgSourceFrom = (bg) => {
  const bgManifest = localiseManifest(bg.manifest, "bg");
  return {
    values: initialValuesFromManifest(bgManifest, bg.page),
    // The Bulgarian fallbacks, not this form's: a field of the form being
    // edited carries the English copy — translating that from Bulgarian is a
    // no-op that overwrites a real translation with itself.
    fallbacks: bgManifest.fields.reduce((acc, item) => {
      acc[item.path] = item.fallback;
      return acc;
    }, {}),
  };
};

/** One part of one list item in the Bulgarian source: the draft's, else the site's. */
const listPartText = ({ path, id, key }, bgDraft, fallbacks) => {
  const itemIn = (value) =>
    Array.isArray(value?.items)
      ? value.items.find((item) => item.id === id)
      : undefined;
  return itemIn(bgDraft[path])?.[key] || itemIn(fallbacks[path])?.[key] || "";
};

// The preview link lands on the marketing site of *this* environment — derived
// from the API origin in helpers/landingUrl, never a hard-coded host (DAB-845).
const previewUrl = (route, lang) => landingPageUrl(route, lang);

/**
 * The form for one page in one language.
 *
 * Saving writes the draft only. Publishing copies that draft into the snapshot
 * the Astro build reads — so an edit reaches the live site at the next build,
 * not at save time.
 */
export default function PageEditor({
  pageKey,
  lang,
  onBack,
  onStatusChange,
  onLangChange,
}) {
  const [manifest, setManifest] = useState(null);
  const [page, setPage] = useState(null);
  const [values, setValues] = useState({});
  const [savedValues, setSavedValues] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isBusy, setIsBusy] = useState(false);
  const [loadError, setLoadError] = useState(null);
  // Section names whose panels are open. Seeded per load from storage or from
  // which sections hold edits; every later change is the editor's own.
  const [openSections, setOpenSections] = useState([]);
  const [query, setQuery] = useState("");
  // `list` fields on a non-Bulgarian tab (DAB-852): the Bulgarian draft of each
  // list, keyed by path — that document owns order and hiding, so the English
  // form shows its structure read-only.
  const [structures, setStructures] = useState({});
  // The rendered Collapse, so a chip can scroll its panel into view without
  // holding a ref per section.
  const sectionsRef = useRef(null);

  // Four buttons do not fit across 375px without wrapping into a bar that eats
  // half the screen, so below `md` all but Publish move into a menu.
  const screens = Grid.useBreakpoint();
  const isNarrow = !screens.md;

  // Live draft preview (DAB-861). Side by side from `xl` (1200px): below that
  // an editor column and a readable page do not both fit, so a tab switch
  // shows one at a time — the frame stays mounted so saves still reload it.
  const live = useLivePreview({ pageKey, lang, onFocusField: focusFromPreview });
  // Click-to-edit (DAB-902): the field the preview last asked for, and a
  // nonce so clicking the same text twice focuses it twice. Cleared once the
  // field has focused, so a field that remounts later (search cleared, list
  // item re-expanded, language switched) does not grab focus again.
  const [focusRequest, setFocusRequest] = useState(null);
  const focusNonceRef = useRef(0);
  const clearFocusRequest = useCallback(() => setFocusRequest(null), []);
  const isSplit = Boolean(screens.xl);
  const [mobileTab, setMobileTab] = useState("edit");
  const toggleLivePreview = () => {
    if (live.isOpen) {
      live.close();
      setMobileTab("edit");
    } else {
      live.open();
      setMobileTab("preview");
    }
  };
  // Guards against out-of-order loads: rapid language switches must not let a
  // slow earlier response overwrite the state of the one requested last.
  const loadTokenRef = useRef(0);

  const load = useCallback(async () => {
    const token = ++loadTokenRef.current;
    setIsLoading(true);
    setLoadError(null);
    // A query from the previous page/language would filter the fresh form —
    // an EN page opening straight to "no fields match" looks broken.
    setQuery("");
    setFocusRequest(null);
    try {
      const data = await fetchPageForEditor(pageKey, lang);
      if (token !== loadTokenRef.current) return;
      // Resolve each field's committed value for the language being edited
      // before anything reads it. The manifest carries `fallback: {bg, en}`
      // (DAB-836); every consumer below — placeholder, seed, "върни текста от
      // сайта", the equals-fallback normalisation — expects a single value.
      const manifestForLang = localiseManifest(data.manifest, lang);
      const initial = initialValuesFromManifest(manifestForLang, data.page);

      const listFields = data.manifest.fields.filter(
        (field) => field.kind === "list",
      );
      let nextStructures = {};
      bgSource.current = null;
      if (lang !== "bg" && listFields.length) {
        const bg = await fetchPageForEditor(pageKey, "bg");
        if (token !== loadTokenRef.current) return;
        // Fetched anyway for the structure, so the translate button reuses it.
        bgSource.current = bgSourceFrom(bg);
        nextStructures = Object.fromEntries(
          listFields.map((field) => [
            field.path,
            bg.page?.draftFields?.[field.path] ?? null,
          ]),
        );
      }
      setManifest(manifestForLang);
      setPage(data.page);
      setValues(initial);
      setSavedValues(initial);
      setStructures(nextStructures);
      setOpenSections(
        initialOpenSections(pageKey, groupFields(data.manifest), initial),
      );
    } catch (error) {
      if (token !== loadTokenRef.current) return;
      setLoadError(errorMessage(error));
    } finally {
      if (token === loadTokenRef.current) setIsLoading(false);
    }
  }, [pageKey, lang]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Compared as payloads, not as raw form state: opening a field seeds its input
   * with the site's committed text, and that seeding normalises back to cleared.
   * Comparing the raw values would report "Unsaved changes" for a field that was
   * merely looked at.
   */
  const isDirty = useMemo(() => {
    if (!manifest) return false;
    return (
      JSON.stringify(payloadFromValues(manifest, values, structures)) !==
      JSON.stringify(payloadFromValues(manifest, savedValues, structures))
    );
  }, [manifest, values, savedValues, structures]);

  const groups = useMemo(
    () => (manifest ? groupFields(manifest) : []),
    [manifest],
  );

  const isSearching = query.trim().length > 0;

  /**
   * What the Collapse renders. With a query active this is the matching fields
   * only — one array either way, so there is a single rendering path and the
   * section index stays honest about what is on screen.
   */
  // Filtered against the values as they were when the query last changed, NOT
  // the live draft: matching live text would unmount the very field being
  // edited on the first keystroke that breaks the substring — losing focus and
  // snapping the field back to its read-only card.
  const valuesRef = useRef(values);
  valuesRef.current = values;
  const visibleGroups = useMemo(
    () => filterGroups(groups, valuesRef.current, query),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [groups, query],
  );

  // A search shows its results open: hiding a match behind a collapsed header
  // would make the search look broken.
  const activeSections = isSearching
    ? visibleGroups.map((group) => group.name)
    : openSections;

  const persistOpenSections = (names) => {
    setOpenSections(names);
    writeOpenSections(pageKey, names);
  };

  const onSectionsChange = (keys) => {
    // While searching the open set is dictated by the results, so a toggle there
    // is not a preference worth remembering.
    if (isSearching) return;
    persistOpenSections(Array.isArray(keys) ? keys : [keys]);
  };

  /** Chip tap: open that section (if it is closed) and bring it into view. */
  const goToSection = (name, index) => {
    if (!isSearching && !openSections.includes(name)) {
      persistOpenSections([...openSections, name]);
    }
    // The panel wrapper is in the DOM whether or not it is expanded, so this
    // needs no wait. Guarded because jsdom has no scrollIntoView.
    const node = sectionsRef.current?.querySelector(`.mp-section--${index}`);
    if (node?.scrollIntoView)
      node.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  /**
   * Click-to-edit (DAB-902): the editor clicked `path`'s text in the preview.
   * Show the editor (below xl the preview is a tab), clear a search that hides
   * the field, open its section, and let the field focus itself.
   *
   * A declaration, not a const: `useLivePreview` above takes it before this
   * point in the render. It only runs later, from a message, by which time
   * everything it reads is set; the hook keeps the latest one in a ref.
   */
  function focusFromPreview(path) {
    const target = manifest && focusTargetFor(manifest.fields, path);
    const group =
      target &&
      groups.find((item) =>
        item.fields.some((field) => field.path === target.fieldPath),
      );
    if (!group) return;
    const isVisible = visibleGroups.some((item) =>
      item.fields.some((field) => field.path === target.fieldPath),
    );
    if (isSearching && !isVisible) setQuery("");
    if (!openSections.includes(group.name)) {
      persistOpenSections([...openSections, group.name]);
    }
    setMobileTab("edit");
    focusNonceRef.current += 1;
    setFocusRequest({ ...target, nonce: focusNonceRef.current });
  }

  /**
   * One handler per path, created once and reused. A fresh closure per render
   * would change every `FieldInput`'s props on each keystroke and defeat the
   * memoization there — with 64 fields on the homepage, that is the difference
   * between re-rendering one input and re-rendering all of them.
   */
  const handlers = useRef(new Map());
  const changeField = (path) => {
    if (!handlers.current.has(path)) {
      handlers.current.set(path, (value) =>
        setValues((current) => ({ ...current, [path]: value })),
      );
    }
    return handlers.current.get(path);
  };

  /**
   * The Bulgarian draft, fetched at most once per editor session.
   *
   * A ref rather than state: nothing renders from it, and putting it in state
   * would re-render all 64 fields the first time any assist button is pressed.
   */
  const bgSource = useRef(null);

  /**
   * Suggest a translation of one field from the Bulgarian version.
   *
   * Only ever returns text — filling the input and saving stay with the editor,
   * so a bad suggestion is a keystroke to undo rather than a published page.
   *
   * The source is the Bulgarian *draft* when there is one, and the field's
   * fallback otherwise: an empty BG draft means that field still shows the copy
   * committed in hpn-landing, which is the Bulgarian text on the live site and
   * exactly what should be translated.
   *
   * Undefined while editing Bulgarian — there is nothing to translate from.
   */
  const assist = useMemo(() => {
    if (lang === "bg") return undefined;

    return async (field) => {
      if (!bgSource.current) {
        bgSource.current = bgSourceFrom(await fetchPageForEditor(pageKey, "bg"));
      }

      const { values: bgDraft, fallbacks } = bgSource.current;
      // A list item's part arrives with `listSource` — its text lives inside
      // the Bulgarian list value, not at a manifest path of its own.
      const source = String(
        (field.listSource
          ? listPartText(field.listSource, bgDraft, fallbacks)
          : bgDraft[field.path] || fallbacks[field.path]) || "",
      ).trim();
      if (!source)
        throw new Error("Българската версия на това поле е празна");

      return assistTranslate(source, lang, "bg");
    };
  }, [lang, pageKey]);

  const save = async () => {
    const token = loadTokenRef.current;
    const saved = await savePageDraft(
      pageKey,
      lang,
      payloadFromValues(manifest, values, structures),
    );
    // An autosave does not lock the bar, so the editor may have moved to
    // another language while this was in flight: the draft is stored, but its
    // response must not overwrite the state of the page loaded since.
    if (token !== loadTokenRef.current) return;
    setPage(saved);
    setSavedValues(values);
    // Every successful save (Save, Publish, autosave) re-renders the frame.
    live.notifySaved();
  };

  // Autosaves the draft ~1500 ms after the last edit while the live preview
  // pane is open (DAB-875), through this same `save()`. `signal` is `values`
  // itself: its identity changes on every keystroke, which is what re-arms
  // the debounce — the hook never reads it.
  const autosave = useDraftAutosave({
    isOpen: live.isOpen,
    isDirty,
    isBusy,
    signal: values,
    save,
    resetKey: `${pageKey}|${lang}`,
  });

  const onSave = async () => {
    // Manual Save must not race an autosave already writing this draft.
    await autosave.awaitIdle();
    setIsBusy(true);
    try {
      await save();
      message.success("Черновата е запазена");
    } catch (error) {
      message.error(`Неуспешно запазване: ${errorMessage(error)}`);
    } finally {
      setIsBusy(false);
    }
  };

  const onPublish = async () => {
    // Same reasoning as onSave: let a running autosave land first.
    await autosave.awaitIdle();
    setIsBusy(true);
    try {
      // Publish snapshots whatever is stored as the draft, so an unsaved form
      // would publish the *previous* text. Saving first is what makes the
      // button mean what it says.
      await save();
      const published = await setPageStatus(pageKey, lang, "published");
      setPage(published);
      message.success("Публикувано — сайтът ще се обнови при следващото изграждане");
      onStatusChange?.();
    } catch (error) {
      message.error(`Неуспешно публикуване: ${errorMessage(error)}`);
    } finally {
      setIsBusy(false);
    }
  };

  /**
   * Publish, after showing what it will change.
   *
   * The list is a real delta: the editor fetch returns `publishedFields` next to
   * the draft, so the confirmation can name what will differ on the site rather
   * than what merely differs from the built-in copy.
   */
  const confirmPublish = () => {
    const changes = publishChangeGroups(
      manifest,
      values,
      page?.publishedFields,
    );
    const count = publishChangeCount(changes);

    Modal.confirm({
      title: count
        ? `Ще публикуваш ${count} ${count === 1 ? "промяна" : "промени"}:`
        : "Публикуване без промени",
      content: count ? (
        <Space direction="vertical" size={4} style={{ width: "100%" }}>
          {changes.map((group) => (
            <div key={group.name}>
              <Text strong>{group.name}:</Text>{" "}
              <Text>{group.labels.join(", ")}</Text>
            </div>
          ))}
        </Space>
      ) : (
        "Няма променени полета — страницата ще се публикува с текста от сайта."
      ),
      okText: "Да, публикувай",
      cancelText: "Отказ",
      onOk: onPublish,
    });
  };

  const onUnpublish = () => {
    Modal.confirm({
      title: "Свали страницата от сайта?",
      content:
        "Страницата отново ще показва текста, вграден в сайта. Черновата ти се запазва.",
      okText: "Свали от сайта",
      cancelText: "Отказ",
      onOk: async () => {
        setIsBusy(true);
        try {
          const unpublished = await setPageStatus(pageKey, lang, "draft");
          setPage(unpublished);
          message.success("Свалена от сайта");
          onStatusChange?.();
        } catch (error) {
          message.error(`Неуспешно сваляне от сайта: ${errorMessage(error)}`);
        } finally {
          setIsBusy(false);
        }
      },
    });
  };

  /**
   * One guard for every way out of an unsaved form — leaving the screen and
   * switching language both discard the same edits, so they ask the same
   * question in the same words.
   */
  const confirmDiscard = (onOk) => {
    Modal.confirm({
      title: "Има незапазени промени",
      content: "Ако продължиш, незапазените промени ще бъдат загубени.",
      okText: "Продължи",
      cancelText: "Отказ",
      onOk,
    });
  };

  const goBack = () => (isDirty ? confirmDiscard(onBack) : onBack());

  /**
   * The language switch lives here as well as on the list: switching is the
   * commonest thing to do next after editing one language, and going back only
   * to come forward again loses your place. The state stays with the list, which
   * owns it — `lang` changing re-runs `load` and the editor refills itself.
   */
  const changeLang = (next) => {
    // isBusy: a save or publish is resolving against the CURRENT language —
    // switching now would let it land its result on the other language's form.
    if (next === lang || !onLangChange || isBusy) return;
    if (!isDirty) return onLangChange(next);
    confirmDiscard(() => onLangChange(next));
  };

  if (isLoading) return <LoadingCard />;

  if (loadError) {
    return (
      <div style={{ padding: 16 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={onBack}
          style={{ marginBottom: 16 }}
        >
          Назад
        </Button>
        <Alert
          type="error"
          showIcon
          message="Неуспешно отваряне на страницата"
          description={loadError}
          action={<Button onClick={load}>Опитай отново</Button>}
        />
      </div>
    );
  }

  const isPublished = page?.status === "published";

  // The sticky bar's status text: autosave gets its own quiet wording so it
  // never looks like the "Draft saved" toast a manual Save shows — that toast
  // firing on every pause in typing would be noise, not feedback.
  const statusText = () => {
    if (live.isOpen && autosave.isSaving) return "Запазва се…";
    return isDirty ? "Незапазени промени" : "Всички промени са запазени";
  };

  const editor = (
    <div
      style={{
        padding: 16,
        paddingBottom: 96,
        maxWidth: 720,
        margin: "0 auto",
      }}
    >
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={goBack}
        disabled={isBusy}
        style={{ marginBottom: 12 }}
      >
        Назад
      </Button>

      <Space
        direction="vertical"
        size={4}
        style={{ width: "100%", marginBottom: 16 }}
      >
        <Title level={4} style={{ margin: 0 }}>
          {manifest.label}
        </Title>
        <Space wrap size={8}>
          <Tag color={lang === "bg" ? "blue" : "purple"}>
            {lang.toUpperCase()}
          </Tag>
          <Tag color={isPublished ? "green" : "default"}>
            {isPublished ? "Редактирана" : "Текст от сайта"}
          </Tag>
          <a
            href={previewUrl(manifest.route, lang)}
            target="_blank"
            rel="noreferrer"
          >
            Преглед на страницата
          </a>
          {live.isAvailable && (
            <Button
              className="mp-live__toggle"
              type={live.isOpen ? "primary" : "default"}
              aria-pressed={live.isOpen}
              onClick={toggleLivePreview}
            >
              Жив преглед
            </Button>
          )}
        </Space>
        <Text type="secondary">
          Всяко поле показва текста от сайта, докато не го редактирате.
          Публикуването влиза в сила при следващото изграждане на сайта.
        </Text>
        {onLangChange && (
          <Segmented
            options={LANGUAGES}
            value={lang}
            onChange={changeLang}
            disabled={isBusy}
            block
            style={{ marginTop: 8 }}
          />
        )}
        <Input.Search
          allowClear
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Търси по заглавие или текст"
          style={CONTROL}
        />
      </Space>

      <div className="mp-section-index">
        {visibleGroups.map((group, index) => {
          const count = overriddenCount(group.fields, values);
          return (
            <Button
              key={group.name}
              size="small"
              className="mp-section-index__chip"
              onClick={() => goToSection(group.name, index)}
            >
              <Space size={6}>
                <span>{group.name}</span>
                {count > 0 && <Tag color="orange">{count}</Tag>}
              </Space>
            </Button>
          );
        })}
      </div>

      <div ref={sectionsRef}>
        {visibleGroups.length === 0 ? (
          <Text type="secondary">
            Няма полета, които да съвпадат с търсенето.
          </Text>
        ) : (
          <Collapse
            activeKey={activeSections}
            onChange={onSectionsChange}
            items={visibleGroups.map((group, index) => {
              // Recomputed on every value change, which is the point: the badge
              // is how a collapsed group says it holds edits.
              const badge = overriddenLabel(
                overriddenCount(group.fields, values),
              );
              return {
                key: group.name,
                className: `mp-section mp-section--${index}`,
                label: (
                  <Space size={8} wrap>
                    <span>{group.name}</span>
                    {badge && <Tag color="orange">{badge}</Tag>}
                  </Space>
                ),
                children: group.fields.map((field) =>
                  field.kind === "list" ? (
                    <ListInput
                      key={field.path}
                      field={field}
                      value={values[field.path]}
                      onChange={changeField(field.path)}
                      lang={lang}
                      structure={structures[field.path]}
                      onAssist={assist}
                      focus={
                        focusRequest?.fieldPath === field.path
                          ? focusRequest
                          : undefined
                      }
                      onFocused={clearFocusRequest}
                    />
                  ) : (
                    <FieldInput
                      key={field.path}
                      field={field}
                      value={values[field.path]}
                      onChange={changeField(field.path)}
                      onAssist={assist}
                      focusNonce={
                        focusRequest?.fieldPath === field.path
                          ? focusRequest.nonce
                          : undefined
                      }
                      onFocused={clearFocusRequest}
                    />
                  ),
                ),
              };
            })}
          />
        )}
      </div>

      <Card
        size="small"
        style={{
          position: "sticky",
          bottom: 0,
          marginTop: 16,
          borderRadius: 8,
          boxShadow: "0 -2px 8px rgba(0,0,0,0.08)",
        }}
      >
        <Space wrap style={{ width: "100%", justifyContent: "space-between" }}>
          <Text type="secondary">{statusText()}</Text>
          <Space wrap>
            {isNarrow ? (
              <Dropdown
                trigger={["click"]}
                disabled={isBusy}
                menu={{
                  items: [
                    {
                      key: "save",
                      label: "Запази чернова",
                      disabled: !isDirty || isBusy,
                    },
                    ...(isPublished
                      ? [
                          {
                            key: "unpublish",
                            label: "Свали от сайта",
                            danger: true,
                            disabled: isBusy,
                          },
                        ]
                      : []),
                  ],
                  onClick: ({ key }) => {
                    if (key === "save") onSave();
                    else if (key === "unpublish") onUnpublish();
                  },
                }}
              >
                <Button
                  aria-label="Още действия"
                  icon={<EllipsisOutlined />}
                  disabled={isBusy}
                  style={CONTROL}
                />
              </Dropdown>
            ) : (
              <>
                {isPublished && (
                  <Button
                    danger
                    onClick={onUnpublish}
                    disabled={isBusy}
                    style={CONTROL}
                  >
                    Свали от сайта
                  </Button>
                )}
                <Button
                  onClick={onSave}
                  disabled={!isDirty || isBusy}
                  loading={isBusy}
                  style={CONTROL}
                >
                  Запази чернова
                </Button>
              </>
            )}
            <Button
              type="primary"
              onClick={confirmPublish}
              disabled={isBusy}
              loading={isBusy}
              style={CONTROL}
            >
              Публикувай
            </Button>
          </Space>
        </Space>
      </Card>
    </div>
  );

  // One wrapper whether the preview is open or not, with the editor always in
  // the same slot: swapping the root element type would make React rebuild
  // the whole editor (scroll, focus, open sections, pickers, uploads) on every
  // toggle, and when a 503 hides the feature mid-session.
  const isStacked = live.isOpen && !isSplit;
  const layout = !live.isOpen ? "mp-live--closed" : isSplit ? "mp-live--split" : "mp-live--stacked";
  const hiddenWhen = (isHidden) => (isHidden ? " mp-live__hidden" : "");
  return (
    <div className={`mp-live ${layout}`}>
      {isStacked && (
        <div className="mp-live__tabs">
          <Segmented
            block
            value={mobileTab}
            onChange={setMobileTab}
            options={[
              { label: "Редакция", value: "edit" },
              { label: "Преглед", value: "preview" },
            ]}
          />
        </div>
      )}
      <div className={`mp-live__editor${hiddenWhen(isStacked && mobileTab !== "edit")}`}>
        {editor}
      </div>
      {live.isOpen && (
        <div
          className={`mp-live__pane${hiddenWhen(isStacked && mobileTab !== "preview")}`}
          data-testid="live-preview-pane"
        >
          <LivePreviewPane
            state={live.state}
            frameRef={live.frameRef}
            onRetry={live.retry}
          />
        </div>
      )}
    </div>
  );
}
